import React, { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link, NavLink, Redirect, Route, Switch } from 'react-router-dom';

import AcadlyCourses from 'components/Admin/AcadlyCourses';
import AllCourses from 'components/Admin/AllCourses';
import AllCoursesAttendance from 'components/Admin/AllCoursesAttendance';
import AllStudentAttendance from 'components/Admin/AllStudentAttendance';
import CourseAnalytics from 'components/Admin/CourseAnalytics';
import FacultyAccount from 'components/Admin/FacultyAccount';
import StudentAccount from 'components/Admin/StudentAccount';
import StudentAnalytics from 'components/Admin/StudentAnalytics';
import EmptyState from 'components/EmptyState';
import Tabs, { TabsProps } from 'components/Tabs';
import Course from 'containers/Course';
import { ROUTES } from 'routes';

import { ReactComponent as AttendanceIcon } from 'assets/attendance_active.svg';
import { ReactComponent as CoursesIcon } from 'assets/courses_active.svg';
import { ReactComponent as InsightsIcon } from 'assets/insights_active.svg';
import { ReactComponent as LogoutIcon } from 'assets/logout_active.svg';
import { ReactComponent as MembersIcon } from 'assets/members_active.svg';
import { ReactComponent as SettingsIcon } from 'assets/settings_active.svg';

type AdminSection = 'accounts' | 'courses' | 'analytics' | 'attendance' | 'settings';

interface RouteParams {
  section: AdminSection;
}

type AdminDashboardProps = RouteComponentProps<RouteParams>;

interface AdminDashboardState {
  activeTab: AdminSection;
  activeSubTab: {
    accounts: 'fac-accounts' | 'std-accounts';
    courses: 'all-courses' | 'acadly-courses';
    analytics: 'course-analytics' | 'student-analytics';
    attendance: 'attendance-class' | 'attendance-students';
    settings: 'settings-one' | 'settings-two';
  };
  hideTabHeader: boolean;
}

class AdminDashboard extends Component<AdminDashboardProps, AdminDashboardState> {
  constructor(props: AdminDashboardProps) {
    super(props);
    this.state = {
      activeTab: this.props.match.params.section || 'accounts',
      activeSubTab: {
        accounts: 'fac-accounts',
        courses: 'all-courses',
        analytics: 'course-analytics',
        attendance: 'attendance-class',
        settings: 'settings-one',
      },
      hideTabHeader: false,
    };
  }

  componentDidUpdate(prevProps: AdminDashboardProps) {
    if (this.props.location !== prevProps.location) {
      this.setState({ activeTab: this.props.match.params.section });
    }
  }

  toggleTabHeader = () => {
    this.setState({ hideTabHeader: !this.state.hideTabHeader });
  };

  onFacultyImportDone = () => {
    // TODO: implement it
  };

  accountsTab = (): TabsProps => {
    return {
      sections: [
        {
          header: 'Faculty Accounts',
          onclick: () =>
            this.setState({
              activeSubTab: {
                ...this.state.activeSubTab,
                accounts: 'fac-accounts',
              },
            }),
          key: 'fac-account',
          active: this.state.activeSubTab.accounts === 'fac-accounts',
          body: <FacultyAccount />,
        },
        {
          header: 'Student Accounts',
          onclick: () =>
            this.setState({
              activeSubTab: {
                ...this.state.activeSubTab,
                accounts: 'std-accounts',
              },
            }),
          key: 'std-account',
          active: this.state.activeSubTab.accounts === 'std-accounts',
          body: <StudentAccount />,
        },
      ],
    };
  };

  CoursesTab = (): TabsProps => ({
    sections: [
      {
        header: 'Courses on Acadly',
        hideTabHeader: this.state.hideTabHeader,
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              courses: 'acadly-courses',
            },
          }),
        key: 'acadly-courses',
        active: this.state.activeSubTab.courses === 'acadly-courses',
        body: <AcadlyCourses />,
      },
      {
        header: 'All Courses',
        hideTabHeader: this.state.hideTabHeader,
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              courses: 'all-courses',
            },
          }),
        key: 'all-courses',
        active: this.state.activeSubTab.courses === 'all-courses',
        body: <AllCourses />,
      },
    ],
  });

  AnalyticsTab = (): TabsProps => ({
    sections: [
      {
        header: 'Course Insights',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              analytics: 'course-analytics',
            },
          }),
        key: 'course-analytics',
        active: this.state.activeSubTab.analytics === 'course-analytics',
        body: <CourseAnalytics />,
      },
      {
        header: 'Student Insights',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              analytics: 'student-analytics',
            },
          }),
        key: 'student-analytics',
        active: this.state.activeSubTab.analytics === 'student-analytics',
        body: <StudentAnalytics />,
      },
    ],
  });

  AttendanceTab = (): TabsProps => ({
    sections: [
      {
        header: 'Courses',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              attendance: 'attendance-class',
            },
          }),
        key: 'attendance-class',
        active: this.state.activeSubTab.attendance === 'attendance-class',
        body: <AllCoursesAttendance />,
        // body: <EmptyState message={'Not enough data to show courses\' analytics'}/>,
      },
      {
        header: 'Individual Students',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              attendance: 'attendance-students',
            },
          }),
        key: 'attendance-students',
        active: this.state.activeSubTab.attendance === 'attendance-students',
        body: <AllStudentAttendance />,
      },
    ],
  });

  // Currently almost a copy of AttendanceTab
  SettingsTab = (): TabsProps => ({
    sections: [
      {
        header: 'Classes',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              settings: 'settings-one',
            },
          }),
        key: 'settings-one',
        active: this.state.activeSubTab.settings === 'settings-one',
        body: <EmptyState />,
      },
      {
        header: 'Individual Students',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              settings: 'settings-two',
            },
          }),
        key: 'settings-two',
        active: this.state.activeSubTab.settings === 'settings-two',
        body: <EmptyState />,
      },
    ],
  });

  render() {
    return (
      <div className='dashboard'>
        <nav className='dashboard__nav'>
          <ul className='nav'>
            <li>
              <NavLink activeClassName='nav__item--active' className='nav__item' to={ROUTES.ADMIN_ACCOUNTS}>
                <MembersIcon className='nav__icon' /> Accounts
              </NavLink>
            </li>
            <li>
              <NavLink activeClassName='nav__item--active' className='nav__item' to={ROUTES.ADMIN_COURSES}>
                <CoursesIcon className='nav__icon' /> Courses
              </NavLink>
            </li>
            <li>
              <NavLink activeClassName='nav__item--active' className='nav__item' to={ROUTES.ADMIN_INSIGHTS}>
                <InsightsIcon className='nav__icon' /> Insights
              </NavLink>
            </li>
            <li>
              <NavLink activeClassName='nav__item--active' className='nav__item' to={ROUTES.ADMIN_ATTENDANCE}>
                <AttendanceIcon className='nav__icon' /> Attendance
              </NavLink>
            </li>
            {false && (
              <li>
                <NavLink
                  activeClassName='nav__item--active'
                  className='nav__item'
                  to={ROUTES.ADMIN_ATTENDANCE}
                >
                  <SettingsIcon className='nav__icon' /> Settings
                </NavLink>
              </li>
            )}
            <li>
              <Link className='nav__item' to={ROUTES.LOG_OUT}>
                <LogoutIcon className='nav__icon' /> Log Out
              </Link>
            </li>
          </ul>
        </nav>
        <main className='dashboard__body'>
          <Switch>
            <Route path={ROUTES.ADMIN_ACCOUNTS}>
              <Tabs {...this.accountsTab()} />
            </Route>
            <Route path={ROUTES.ADMIN_COURSES_CREATE}>
              <Course />
            </Route>
            <Route path={ROUTES.ADMIN_COURSES}>
              <Tabs {...this.CoursesTab()} />
            </Route>
            <Route path={ROUTES.ADMIN_INSIGHTS}>
              <Tabs {...this.AnalyticsTab()} />
            </Route>
            <Route path={ROUTES.ADMIN_ATTENDANCE}>
              <Tabs {...this.AttendanceTab()} />
            </Route>
            <Route path={ROUTES.ADMIN_SETTINGS}>
              <Tabs {...this.AnalyticsTab()} />
            </Route>
            <Redirect to={ROUTES.ADMIN_COURSES} />
          </Switch>
        </main>
      </div>
    );
  }
}

export default withRouter(AdminDashboard);
